/**
 * Constants for quality calculation
 */
export const QUALITY = {
    BASE_VALUE: 50,
    MESSAGE: {
        SHORT_THRESHOLD: 200,
        LONG_THRESHOLD: 500,
        SHORT_FACTOR: 0.2,
        SHORT_OFFSET: -40,
        LONG_FACTOR: 0.133,
        COUNTRY_MULTIPLIER: 1,
        TOUR_MULTIPLIER: 0.5
    },
    BUDGET: {
        LOWER_FACTOR: 2,
        HIGHER_FACTOR: 0.4,
        HIGHER_MAX: 20
    },
    PEOPLE: {
        IDEAL_GROUP_SIZE: 2,
        BONUS: 10
    },
    TOUR: {
        BONUS: 10
    },
    MAX_VALUE: 100,
    MIN_VALUE: 0
};

/**
 * Calculate quality based on message length
 */
export function calculateMessageQuality(message, isCountry) {
    if (!message) {
        return null; // null indicates we should return 0 for the entire quality
    }

    const messageLength = message.length;
    let quality;

    if (messageLength > QUALITY.MESSAGE.LONG_THRESHOLD) {
        return QUALITY.MAX_VALUE; // Immediate max quality for very long messages
    } else if (messageLength <= QUALITY.MESSAGE.SHORT_THRESHOLD) {
        quality = messageLength * QUALITY.MESSAGE.SHORT_FACTOR + QUALITY.MESSAGE.SHORT_OFFSET;
    } else {
        quality = (messageLength - QUALITY.MESSAGE.SHORT_THRESHOLD) * QUALITY.MESSAGE.LONG_FACTOR;
    }

    // Apply multiplier based on whether it's a country or tour request
    return quality * (isCountry ? QUALITY.MESSAGE.COUNTRY_MULTIPLIER : QUALITY.MESSAGE.TOUR_MULTIPLIER);
}

/**
 * Calculate quality based on budget
 */
export function calculateBudgetQuality(userBudget, advisedBudget) {
    if (!userBudget || !advisedBudget) {
        return 0;
    }

    const budgetDifferencePercent = ((userBudget - advisedBudget) / advisedBudget) * 100;
    
    if (userBudget < advisedBudget) {
        return QUALITY.BUDGET.LOWER_FACTOR * budgetDifferencePercent;
    } else if (userBudget === advisedBudget) {
        return 0;
    } else {
        const quality = QUALITY.BUDGET.HIGHER_FACTOR * budgetDifferencePercent;
        return Math.min(quality, QUALITY.BUDGET.HIGHER_MAX);
    }
}

/**
 * Calculate quality based on number of people traveling
 */
export function calculatePeopleQuality(groupSize) {
    const totalPeople = groupSize.adults + groupSize.children + groupSize.infants;
    return totalPeople === QUALITY.PEOPLE.IDEAL_GROUP_SIZE ? QUALITY.PEOPLE.BONUS : 0;
}

/**
 * Calculate quality based on whether it's a tour
 */
export function calculateTourQuality(tour) {
    return tour?.uid ? QUALITY.TOUR.BONUS : 0;
}

/**
 * Calculate the final quality value
 */
export function calculateTotalQuality(messageQuality, budgetQuality, peopleQuality, tourQuality, debug = false) {
    // Special case: if messageQuality is null, return 0
    if (messageQuality === null) {
        return 0;
    }

    let totalQuality = QUALITY.BASE_VALUE + messageQuality + budgetQuality + peopleQuality + tourQuality;
    totalQuality = Math.min(Math.max(totalQuality, QUALITY.MIN_VALUE), QUALITY.MAX_VALUE);
    
    if (debug) {
        console.log({
        baseValue: QUALITY.BASE_VALUE,
        messageQuality,
        budgetQuality,
        peopleQuality,
        tourQuality,
        totalQuality
        });
    }
    
    return totalQuality;
}
